import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

const Articles = () => {
  const { t, isRTL, lang } = useLanguage();
  const headerRef = useReveal();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/articles", { params: { limit: 20 } }).then((r) => {
      const all = r.data.items || [];
      const pinned = all.filter((a) => a.pinned).slice(0, 1);
      const rest = all.filter((a) => !a.pinned).slice(0, 3);
      setItems([...pinned, ...rest].slice(0, 4));
    }).catch(() => {});
  }, []);

  return (
    <section id="articles" data-testid="articles-section" className="relative section-y bg-ink-soft border-t border-white/5">
      <div className="container-x">
        <div ref={headerRef} className="reveal max-w-3xl mb-20">
          <div className="flex items-center gap-4 mb-6">
            <span className="hairline" />
            <span className="overline">{t.articles.overline}</span>
          </div>
          <h2
            className={`${isRTL ? "font-arabic font-black leading-[1.15]" : "font-serif leading-[1.05]"} text-bone text-3xl sm:text-4xl lg:text-5xl tracking-tight text-balance`}
            data-testid="articles-heading"
          >
            {t.articles.heading}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8 auto-rows-[minmax(0,1fr)]">
          {items.map((a, i) => {
            const large = i === 0 || i === 3;
            const localized = a[lang] || a.ar || a.en || {};
            return (
              <ArticleCard
                key={a.id}
                index={i}
                article={a}
                localized={localized}
                large={large}
                isRTL={isRTL}
                readMore={t.articles.readMore}
              />
            );
          })}
        </div>

        <div className="flex justify-center mt-16">
          <Link
            to="/articles"
            className="btn-ghost inline-flex items-center gap-3 group"
          >
            <span className={isRTL ? "font-arabic" : ""}>{isRTL ? "المزيد من المقالات" : "More Articles"}</span>
            <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

      </div>
    </section>
  );
};

const ArticleCard = ({ article, localized, large, index, isRTL, readMore }) => {
  const ref = useReveal({ threshold: 0.1 });
  return (
    <Link
      to={`/articles/${article.slug}`}
      ref={ref}
      data-testid={`article-card-${index}`}
      className={`reveal group relative overflow-hidden border border-white/10 hover:border-gold/40 transition-all duration-500 ${
        large ? "lg:col-span-7 min-h-[460px]" : "lg:col-span-5 min-h-[380px]"
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      {article.cover_image ? (
        <img
          src={article.cover_image}
          alt={localized.title || ""}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.08] opacity-80 group-hover:opacity-90"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-ink-soft" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/20" />
      <div className={`absolute inset-0 ${isRTL ? "bg-gradient-to-l" : "bg-gradient-to-r"} from-ink/60 to-transparent`} />

      <div className="relative h-full flex flex-col justify-between p-8 lg:p-12">
        <div className="flex items-center gap-3 text-[11px] tracking-[0.25em] uppercase text-white/60">
          {article.pinned && <span className="text-gold">★</span>}
          <span className="text-gold">{isRTL ? (article.ar?.category || "") : (article.en?.category || "")}</span>
          <span className="w-4 h-px bg-white/30" />
          <span dir="ltr">{(article.publish_date || "").slice(0, 10)}</span>
        </div>

        <div>
          <h3
            className={`${isRTL ? "font-arabic font-bold leading-[1.25]" : "font-serif leading-[1.1]"} text-bone ${
              large ? "text-3xl sm:text-4xl lg:text-5xl" : "text-2xl sm:text-3xl"
            } tracking-tight text-balance group-hover:text-gold transition-colors duration-500`}
          >
            {localized.title || "—"}
          </h3>
          {localized.excerpt && (
            <p className={`mt-4 text-white/60 max-w-xl ${isRTL ? "font-arabic text-base leading-[1.9]" : "text-sm leading-relaxed"}`}>
              {localized.excerpt}
            </p>
          )}
          <div className="mt-6 inline-flex items-center gap-2 text-gold text-xs tracking-[0.25em] uppercase">
            <span>{readMore}</span>
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default Articles;
